import { baseApi, defaultQueryFreshness } from "./baseApi";
import type { SendMessageResponse } from "../../api/conversations";

interface ApiPayload extends Record<string, unknown> {
  message?: string;
}

export const conversationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchConversations: builder.query<unknown, void>({
      query: () => ({ url: "/conversations", method: "GET" }),
      providesTags: [{ type: "Conversations", id: "LIST" }],
      ...defaultQueryFreshness,
    }),
    fetchConversationById: builder.query<unknown, string>({
      query: (id) => ({ url: `/conversations/${id}`, method: "GET" }),
      providesTags: (_res, _err, id) => [
        { type: "Conversations", id: String(id) },
        { type: "Messages", id: `conversation:${id}` },
      ],
      ...defaultQueryFreshness,
    }),
    getUnreadThreadsCount: builder.query<{ count: number }, void>({
      query: () => ({ url: "/conversations/unread-threads-count", method: "GET" }),
      providesTags: [{ type: "Conversations", id: "UNREAD_THREADS_COUNT" }],
      ...defaultQueryFreshness,
    }),
    startConversation: builder.mutation<ApiPayload, { listingId: string; message: string }>({
      query: ({ listingId, message }) => ({ url: "/conversations", method: "POST", data: { listingId, message } }),
      invalidatesTags: [{ type: "Conversations", id: "LIST" }],
    }),
    sendMessage: builder.mutation<ApiPayload | SendMessageResponse, { conversationId: string; message: string; clientMessageId?: string; attachment?: string }>({
      query: ({ conversationId, message, clientMessageId, attachment }) => ({
        url: `/conversations/${conversationId}/messages`,
        method: "POST",
        data: { message, ...(clientMessageId ? { clientMessageId } : {}), ...(attachment ? { attachment } : {}) },
      }),
      invalidatesTags: (_res, _err, { conversationId }) => [
        { type: "Conversations", id: "LIST" },
        { type: "Conversations", id: "UNREAD_THREADS_COUNT" },
        { type: "Conversations", id: String(conversationId) },
        { type: "Messages", id: `conversation:${conversationId}` },
      ],
    }),
    markConversationRead: builder.mutation<
      { ok: boolean; unreadCount: number; conversationId: string },
      { conversationId: string }
    >({
      query: ({ conversationId }) => ({
        url: `/conversations/${conversationId}/read`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, { conversationId }) => [
        { type: "Conversations", id: "LIST" },
        { type: "Conversations", id: "UNREAD_THREADS_COUNT" },
        { type: "Conversations", id: String(conversationId) },
        { type: "Messages", id: `conversation:${conversationId}` },
      ],
    }),
    deleteConversation: builder.mutation<ApiPayload, string>({
      query: (conversationId) => ({ url: `/conversations/${conversationId}`, method: "DELETE" }),
      invalidatesTags: (_res, _err, conversationId) => [
        { type: "Conversations", id: "LIST" },
        { type: "Conversations", id: "UNREAD_THREADS_COUNT" },
        { type: "Conversations", id: String(conversationId) },
      ],
    }),
    deleteMessage: builder.mutation<ApiPayload, { conversationId: string; messageId: string }>({
      query: ({ conversationId, messageId }) => ({
        url: `/conversations/${conversationId}/messages/${messageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_res, _err, { conversationId }) => [
        { type: "Conversations", id: String(conversationId) },
        { type: "Messages", id: `conversation:${conversationId}` },
      ],
    }),
  }),
});

export const {
  useFetchConversationsQuery,
  useFetchConversationByIdQuery,
  useGetUnreadThreadsCountQuery,
  useStartConversationMutation,
  useSendMessageMutation,
  useMarkConversationReadMutation,
  useDeleteConversationMutation,
  useDeleteMessageMutation,
} = conversationsApi;
